<template>
	<base-select searchable clearable avoidDefaultSort :loading="loadingOptions" :options="options" valueProp="id" :label="$i18nHelpers.normalizeProp('label')" v-model="selectValue" @input="onOptionSelected" @search="search($event)" @option:selected="$emit('option:selected', $event)" :placeholder="placeholder">
		<template #option="data" v-if="options && options.length > 0">
			<div>
				<img :id="getPhotoElementId(data.option.value)" class="photo-option" :class="{ 'professional-photo': isProfessionalOption(data.option.value) }" :src="getPhoto(data.option.value)"/> <span>{{data.option.label}}</span>
			</div>
		</template>
	</base-select>
</template>

<script>
import wrapperMixin from '@/mixins/wrapper.mixin'
import BaseSelect from '@/components/base/forms/BaseSelect'
import multipleSearchOptionRepository from '@/repositories/MultipleSearchOptionRepository'
import multipleSearchOptionTypes from '@/constants/multipleSearchOptionTypes'
import { PROFESSIONAL_MALE_DEFAULT_IMAGE } from '@/constants/images'
import { checkIgnoringNonRelevantCharacters } from '@/helpers/string.helpers'
import { INPLACE } from '@/constants/consultationTypes'

const SPECIALTY_PROP = 'specialty'
const PROFESSIONAL_PROP = 'professional'
const SERVICE_PROP = 'service'

const PROPS = [SPECIALTY_PROP, PROFESSIONAL_PROP, SERVICE_PROP]

const messages = {
	es: {
		'multiple-search-selector.placeholder': 'Especialidad, prueba o nombre',
		'multiple-search-selector.placeholder-online': 'Especialidad o nombre',
		'multiple-search-selector.placeholder-360': 'Especialidad',
		'multiple-search-selector.tryOnlineSearch': 'No hemos encontrado resultados para su búsqueda. Haga click en la opción "Online" y revise los resultados para su filtro'
	},
	en: {
		'multiple-search-selector.placeholder': 'Specialty, medical exam or name',
		'multiple-search-selector.placeholder-online': 'Specialty or name',
		'multiple-search-selector.placeholder-360': 'Specialty',
		'multiple-search-selector.tryOnlineSearch': 'We found no results for your search. Click the "Online" option and review the results for your filter.'
	},
	pt: {
		'multiple-search-selector.placeholder': 'Especialidade, exame ou nome',
		'multiple-search-selector.placeholder-online': 'Especialidade ou nome',
		'multiple-search-selector.placeholder-360': 'Especialidade',
		'multiple-search-selector.tryOnlineSearch': 'Não encontramos resultados para sua pesquisa. Clique na opção "Online" e analise os resultados do seu filtro'
	}
}

// Wrapper mixin value: Object:
// {
	// 	specialty: int,
	// 	professional: string,
	// 	service: string
// }

export default {
	mixins: [wrapperMixin(Object)],
	i18n: {
		messages
	},
	components: {
		BaseSelect
	},
	data () {
		return {
			selectValue: null,
			options: [],
			[SPECIALTY_PROP]: null,
			[PROFESSIONAL_PROP]: null,
			[SERVICE_PROP]: null,
			loadingOptions: false
		}
	},
	props: {
		includeServices: Boolean,
		only360Specialties: Boolean,
		appointmentType: Number
	},
	watch: {
		value: {
			handler (value) {
				if (!value || (this.currentSelectedOption && !value[this.currentSelectedOption.type.toLowerCase()])) {
					this.selectValue = null
					this.reset()
				} else {
					this[SPECIALTY_PROP] = value[SPECIALTY_PROP]
					this[PROFESSIONAL_PROP] = value[PROFESSIONAL_PROP]
					this[SERVICE_PROP] = value[SERVICE_PROP]
				}
			},
			immediate: true
		},
		appointmentType () {
			this.loadOptions()
		}
	},
	computed: {
		currentSelectedOption () {
			if (!this.selectValue) return null
			return this.options.find(o => o.id === this.selectValue)
		},
		placeholder () {
			if (this.only360Specialties) {
				return this.$t('multiple-search-selector.placeholder-360')
			} else if (!this.includeServices) {
				return this.$t('multiple-search-selector.placeholder-online')
			} else {
				return this.$t('multiple-search-selector.placeholder')
			}
		}
	},
	methods: {
		reset () {
			for (let prop of PROPS) {
				this[prop] = null
			}
		},
		getPhoto (id) {
			const option = this.options.find(o => o.id === id)
			if (!option) return PROFESSIONAL_MALE_DEFAULT_IMAGE
			return option.photo || PROFESSIONAL_MALE_DEFAULT_IMAGE
		},
		getPhotoElementId (id) {
			return `select-option-photo-${id}`
		},
		onPhotoError (id) {
			const option = this.options.find(o => o.id === id)
			if (option.type === multipleSearchOptionTypes.PROFESSIONAL) {
				this.$nextTick(() => {
					document.getElementById(this.getPhotoElementId(id)).src = PROFESSIONAL_MALE_DEFAULT_IMAGE
				})
			}
		},
		isProfessionalOption (id) {
			if (!this.options) return false
			const option = this.options.find(o => o.id === id)
			return option.type === multipleSearchOptionTypes.PROFESSIONAL
		},
		loadOptions (query = null) {
			this.loadingOptions = true
			multipleSearchOptionRepository.getAll({ query, services: this.includeServices, only360: this.only360Specialties, appointmentType: this.appointmentType }, { loading: false }).then(response => {
				let result = response.data
				if (query) {
					result = [...result.filter(option => checkIgnoringNonRelevantCharacters(this.$i18nHelpers.normalize(option.label), query) > -1)]
					if (query.length > 2 && !result.length && this.appointmentType === INPLACE) {
						this.$store.dispatch('showAlert', {
							text: this.$t('multiple-search-selector.tryOnlineSearch'),
							variant: 'info'
						})
					}
				}
				this.options = result
			})
		},
		search (query) {
			if (this.selectValue) { return }
			this.loadOptions(query && query.length > 2 ? query : null)
		},
		onOptionSelected () {
			this.reset()
			if (this.currentSelectedOption) {
				switch (this.currentSelectedOption.type) {
					case multipleSearchOptionTypes.SPECIALTY:
						this[SPECIALTY_PROP] = this.currentSelectedOption.id
						break
					case multipleSearchOptionTypes.PROFESSIONAL:
						this[PROFESSIONAL_PROP] = this.currentSelectedOption.id
						break
					case multipleSearchOptionTypes.SERVICE:
						this[SERVICE_PROP] = this.currentSelectedOption.id
						break
				}
			}
			this.localValue = {
				[SPECIALTY_PROP]: this.specialty,
				[PROFESSIONAL_PROP]: this.professional,
				[SERVICE_PROP]: this.service
			}
			this.$emit('input', this.localValue)
		}
	},
	mounted () {
		this.loadOptions()
	},
	updated () {
		this.$nextTick(() => {
			this.loadingOptions = false
		})
	}
}
</script>

<style lang="scss">

.photo-option {
	margin-right: 1rem;
	width: 30px;
	height: 30px;

	&.professional-photo {
		border-radius: 100%;
		padding: 0;
	}
}

</style>


// WEBPACK FOOTER //
// src/components/shared/MultipleSearchSelector.vue